import { useState, useRef, useEffect, useCallback, type KeyboardEvent, type ClipboardEvent } from 'react'
import { Send, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useFStore } from '@/stores/useFStore'
import { uploadFoolscapAttachment } from '@/lib/api'

const IMAGE_SIZE_LIMIT = 5 * 1024 * 1024 // 5MB

interface FChatInputProps {
  onSubmit: (content: string) => void
  disabled?: boolean
  placeholder?: string
}

export function FChatInput({ onSubmit, disabled, placeholder }: FChatInputProps) {
  const [value, setValue] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const scrollPositionRef = useRef<number>(0)

  // Attachment paste state
  const [isUploadingImage, setIsUploadingImage] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [uploadSuccess, setUploadSuccess] = useState(false)

  const sessionId = useFStore((state) => state.sessionId)
  const incrementAttachmentVersion = useFStore((state) => state.incrementAttachmentVersion)

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current
    if (textarea) {
      textarea.style.height = 'auto'
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`
    }
  }, [value])

  // Save scroll position before focus (iOS keyboard fix)
  const handleFocus = useCallback(() => {
    scrollPositionRef.current = window.scrollY
    // Use requestAnimationFrame to restore scroll after iOS keyboard causes unwanted scroll
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (Math.abs(window.scrollY - scrollPositionRef.current) > 50) {
          window.scrollTo(0, scrollPositionRef.current)
        }
      })
    })
  }, [])

  const handleSubmit = useCallback(() => {
    if (value.trim() && !disabled) {
      onSubmit(value.trim())
      setValue('')
      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto'
      }
      // Blur on mobile after submit to hide keyboard
      if (window.innerWidth < 768) {
        textareaRef.current?.blur()
      }
    }
  }, [value, disabled, onSubmit])

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Submit on Enter (without shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  const handlePaste = useCallback(async (e: ClipboardEvent<HTMLTextAreaElement>) => {
    if (!sessionId) return

    const items = e.clipboardData?.items
    if (!items) return

    // Look for image items in clipboard
    for (const item of Array.from(items)) {
      if (item.type.startsWith('image/')) {
        e.preventDefault() // Prevent default paste for images

        const blob = item.getAsFile()
        if (!blob) continue

        // Check size limit
        if (blob.size > IMAGE_SIZE_LIMIT) {
          setUploadError('Image exceeds 5MB limit')
          return
        }

        // Create a File object with a proper name
        const extension = item.type.split('/')[1] || 'png'
        const filename = `screenshot-${Date.now()}.${extension}`
        const file = new File([blob], filename, { type: item.type })

        // Upload the image
        setIsUploadingImage(true)
        setUploadError(null)

        try {
          await uploadFoolscapAttachment(sessionId, file)
          // Trigger attachments panel refresh
          incrementAttachmentVersion()
          // Show success briefly
          setUploadSuccess(true)
          setTimeout(() => setUploadSuccess(false), 2000)
        } catch (err) {
          console.error('Screenshot upload failed:', err)
          setUploadError(err instanceof Error ? err.message : 'Upload failed')
        } finally {
          setIsUploadingImage(false)
        }

        return // Only handle first image
      }
    }
  }, [sessionId, incrementAttachmentVersion])

  return (
    <div className="relative">
      {/* Upload indicator */}
      {isUploadingImage && (
        <div className="absolute -top-8 left-0 right-0 flex items-center justify-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="w-3 h-3 animate-spin" />
          <span>Uploading screenshot...</span>
        </div>
      )}

      {/* Upload error */}
      {uploadError && (
        <div className="absolute -top-8 left-0 right-0 flex items-center justify-center gap-2 text-xs text-red-500">
          <span>{uploadError}</span>
        </div>
      )}

      {/* Upload success */}
      {uploadSuccess && (
        <div className="absolute -top-8 left-0 right-0 flex items-center justify-center gap-2 text-xs text-green-600">
          <span>Screenshot added to Attachments</span>
        </div>
      )}

      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onFocus={handleFocus}
        disabled={disabled || isUploadingImage}
        placeholder={placeholder || "Type a message..."}
        rows={1}
        className={cn(
          'w-full resize-none rounded-lg border border-border bg-background pl-4 pr-12 py-3',
          'text-base md:text-sm text-foreground placeholder:text-muted-foreground',
          'focus:outline-none focus:ring-1 focus:ring-foreground/20',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          'transition-colors'
        )}
      />

      {/* Send button - right side */}
      <button
        onClick={handleSubmit}
        disabled={disabled || !value.trim()}
        className={cn(
          'absolute right-2 top-1/2 -translate-y-1/2',
          'p-3 -m-1 rounded-md',
          'text-muted-foreground hover:text-foreground active:text-foreground',
          'disabled:opacity-30 disabled:cursor-not-allowed',
          'transition-colors'
        )}
        aria-label="Send message"
      >
        <Send className="w-5 h-5 md:w-4 md:h-4" strokeWidth={1.5} />
      </button>
    </div>
  )
}
